import {
  PullRequestChecksStatus,
  PullRequestReviewDecision,
} from './doboxExecutor';

export interface PullRequestChecksSummary {
  status: PullRequestChecksStatus;
  failedCount: number;
  pendingCount: number;
  totalCount: number;
}

/** One `statusCheckRollup` entry: a CheckRun (status/conclusion) or a StatusContext (state). */
export interface GhRollupEntry {
  __typename?: string;
  name?: string;
  context?: string;
  workflowName?: string;
  status?: string;
  conclusion?: string;
  state?: string;
  startedAt?: string;
}

const GH_TYPENAME_STATUS_CONTEXT = 'StatusContext';
const GH_CHECK_STATUS_COMPLETED = 'COMPLETED';
const GH_CHECK_STATE_SUCCESS = 'SUCCESS';
const GH_CHECK_STATE_FAILING_VALUES: ReadonlySet<string> = new Set([
  'FAILURE',
  'ERROR',
]);
const GH_CHECK_CONCLUSION_PASSING_VALUES: ReadonlySet<string> = new Set([
  'SUCCESS',
  'SKIPPED',
  'NEUTRAL',
]);
const GH_CHECK_CONCLUSION_FAILING_VALUES: ReadonlySet<string> = new Set([
  'FAILURE',
  'ERROR',
  'TIMED_OUT',
  'CANCELLED',
  'ACTION_REQUIRED',
  'STARTUP_FAILURE',
  'STALE',
]);

enum CheckOutcome {
  PASSING = 'PASSING',
  FAILING = 'FAILING',
  PENDING = 'PENDING',
}

function stateOutcome(state: string | undefined): CheckOutcome {
  if (state === GH_CHECK_STATE_SUCCESS) {
    return CheckOutcome.PASSING;
  }
  if (state && GH_CHECK_STATE_FAILING_VALUES.has(state)) {
    return CheckOutcome.FAILING;
  }
  return CheckOutcome.PENDING;
}

function checkRunOutcome(entry: GhRollupEntry): CheckOutcome {
  if (entry.status !== GH_CHECK_STATUS_COMPLETED) {
    return CheckOutcome.PENDING;
  }
  if (
    entry.conclusion &&
    GH_CHECK_CONCLUSION_PASSING_VALUES.has(entry.conclusion)
  ) {
    return CheckOutcome.PASSING;
  }
  if (
    entry.conclusion &&
    GH_CHECK_CONCLUSION_FAILING_VALUES.has(entry.conclusion)
  ) {
    return CheckOutcome.FAILING;
  }
  return CheckOutcome.PASSING;
}

function entryOutcome(entry: GhRollupEntry): CheckOutcome {
  return entry.__typename === GH_TYPENAME_STATUS_CONTEXT
    ? stateOutcome(entry.state)
    : checkRunOutcome(entry);
}

function entryKey(entry: GhRollupEntry): string {
  return `${entry.__typename ?? ''}|${entry.workflowName ?? ''}|${entry.name ?? entry.context ?? ''}`;
}

function startedAtMs(entry: GhRollupEntry): number {
  const parsed = entry.startedAt ? Date.parse(entry.startedAt) : Number.NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** A re-run leaves the superseded run in the rollup beside the new one (a red run followed by
 *  a green one of the same workflow and name) — only the latest counts. */
function latestPerCheck(entries: GhRollupEntry[]): GhRollupEntry[] {
  const latest = new Map<string, GhRollupEntry>();
  entries.forEach((entry) => {
    const key = entryKey(entry);
    const current = latest.get(key);
    if (!current || startedAtMs(entry) >= startedAtMs(current)) {
      latest.set(key, entry);
    }
  });
  return [...latest.values()];
}

function summarizeOutcomes(outcomes: CheckOutcome[]): PullRequestChecksSummary {
  const failedCount = outcomes.filter((o) => o === CheckOutcome.FAILING).length;
  const pendingCount = outcomes.filter(
    (o) => o === CheckOutcome.PENDING,
  ).length;
  const status = (() => {
    if (outcomes.length === 0) {
      return PullRequestChecksStatus.NONE;
    }
    if (failedCount > 0) {
      return PullRequestChecksStatus.FAILING;
    }
    return pendingCount > 0
      ? PullRequestChecksStatus.PENDING
      : PullRequestChecksStatus.PASSING;
  })();
  return { status, failedCount, pendingCount, totalCount: outcomes.length };
}

/** Pure mapping of gh's `statusCheckRollup`. A non-array (field missing, gh older than the
 *  field) is the same as no checks. */
export function mapChecksRollup(rollup: unknown): PullRequestChecksSummary {
  const entries = Array.isArray(rollup)
    ? (rollup.filter(
        (entry) => typeof entry === 'object' && entry !== null,
      ) as GhRollupEntry[])
    : [];
  return summarizeOutcomes(latestPerCheck(entries).map(entryOutcome));
}

const REVIEW_DECISION_BY_GH_VALUE: ReadonlyMap<
  string,
  PullRequestReviewDecision
> = new Map([
  ['APPROVED', PullRequestReviewDecision.APPROVED],
  ['CHANGES_REQUESTED', PullRequestReviewDecision.CHANGES_REQUESTED],
  ['REVIEW_REQUIRED', PullRequestReviewDecision.REVIEW_REQUIRED],
]);

/** gh reports `''` (or null) when the base branch requires no review. */
export function mapReviewDecision(raw: unknown): PullRequestReviewDecision {
  return (
    (typeof raw === 'string'
      ? REVIEW_DECISION_BY_GH_VALUE.get(raw)
      : undefined) ?? PullRequestReviewDecision.NONE
  );
}

interface GhReviewThread {
  isResolved?: unknown;
  comments?: { nodes?: { author?: { login?: unknown } | null }[] };
}

interface GhReviewThreadsResponse {
  data?: {
    viewer?: { login?: unknown };
    repository?: {
      pullRequest?: { reviewThreads?: { nodes?: unknown } };
    };
  };
}

function isThreadAwaitingReply(
  thread: GhReviewThread,
  viewerLogin: string,
): boolean {
  if (thread.isResolved !== false) {
    return false;
  }
  const lastComment = thread.comments?.nodes?.at(-1);
  if (!lastComment) {
    return false;
  }
  return lastComment.author?.login !== viewerLogin;
}

/** Review threads still waiting on the authenticated gh account: unresolved, and whose last
 *  comment was written by someone else. Logins are compared here and never returned.
 *  `undefined` means the response was unusable, so the caller omits the signal. */
export function countThreadsAwaitingReply(
  response: unknown,
): number | undefined {
  const { data } = (response ?? {}) as GhReviewThreadsResponse;
  const viewerLogin = data?.viewer?.login;
  const threads = data?.repository?.pullRequest?.reviewThreads?.nodes;
  if (
    typeof viewerLogin !== 'string' ||
    !viewerLogin ||
    !Array.isArray(threads)
  ) {
    return undefined;
  }
  return threads.filter(
    (thread) =>
      typeof thread === 'object' &&
      thread !== null &&
      isThreadAwaitingReply(thread as GhReviewThread, viewerLogin),
  ).length;
}
